import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Plus, Minus, Loader2, Check, Navigation, GlassWater, Download } from "lucide-react";
import { MaskLines, Reveal } from "../components/anim";
import { Eyebrow } from "../components/ui-bits";
import useSEO from "../hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", useGrouping: "always" }).format(n);

const PRODOTTI = [
  { id: "spritz", name: "Spritz Classico", tipo: "Premix alla spina" },
  { id: "campari", name: "Spritz Bitter", tipo: "Premix alla spina" },
  { id: "gin-tonic", name: "Gin Tonic", tipo: "Premix alla spina" },
  { id: "gin-lemon", name: "Gin Lemon", tipo: "Premix alla spina" },
  { id: "negroni", name: "Negroni", tipo: "Premix alla spina" },
  { id: "prosecco", name: "Prosecco", tipo: "Alla spina" },
  { id: "vino-bianco", name: "Vino bianco", tipo: "Alla spina" },
  { id: "vino-rosso", name: "Vino rosso", tipo: "Alla spina" },
  { id: "birra", name: "Birra", tipo: "Alla spina" },
  { id: "analcolici", name: "Analcolici", tipo: "Alla spina" },
];

export default function Preventivo() {
  useSEO(
    "Preventivo online: calcola trasferta, spina e servizio | Sprizziamoci",
    "Calcola in autonomia il preventivo del tuo evento: trasferta stradale reale da Siena, fusti da 20 L (circa 100 bicchieri, fusto pagato per intero), ghiaccio incluso. Totale immediato con IVA 22%."
  );
  const [cfg, setCfg] = useState(null);
  const [fusti, setFusti] = useState({});
  const [allestimento, setAllestimento] = useState(0);
  const [q, setQ] = useState("");
  const [sugg, setSugg] = useState([]);
  const [route, setRoute] = useState(null);
  const [loadingRoute, setLoadingRoute] = useState(false);
  const [contact, setContact] = useState({
    nome_cognome: "",
    email: "",
    telefono: "",
    data_evento: "",
    website: "",
  });
  const [sending, setSending] = useState(false);
  const [quoteId, setQuoteId] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/config`)
      .then((r) => setCfg(r.data))
      .catch(() => toast.error("Configurazione non disponibile"));
  }, []);

  useEffect(() => {
    if (!q || q.trim().length < 3 || q === route?.destinazione) {
      setSugg([]);
      return;
    }
    const t = setTimeout(() => {
      axios
        .get(`${API}/geo/search`, { params: { q } })
        .then((r) => setSugg(r.data))
        .catch(() => {});
    }, 450);
    return () => clearTimeout(t);
  }, [q, route]);

  const pickLocation = async (display) => {
    setQ(display);
    setSugg([]);
    setLoadingRoute(true);
    try {
      const { data } = await axios.post(`${API}/geo/route`, { destination: display });
      setRoute(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Calcolo del percorso non riuscito, riprova");
    } finally {
      setLoadingRoute(false);
    }
  };

  if (!cfg)
    return (
      <div className="pt-40 pb-40 text-center text-white/50 text-sm" data-testid="preventivo-loading">
        Caricamento…
      </div>
    );

  const costoServizio = route
    ? Math.round((route.costo_trasferta + cfg.noleggio_fisso + cfg.montaggio_fisso) * 100) / 100
    : 0;
  const prodottiSel = PRODOTTI.filter((p) => (fusti[p.id] || 0) > 0);
  const totFusti = prodottiSel.reduce((s, p) => s + fusti[p.id], 0);
  const costoFusti = totFusti * cfg.prezzo_fusto;
  const totPorzioni = totFusti * cfg.porzioni_fusto;
  const pacchiBicchieri = Math.ceil(totPorzioni / 100);
  const erogatori = prodottiSel.length;
  const banchiExtra = Math.max(0, Math.ceil(erogatori / 4) - 1);
  const voci = [
    ...(route
      ? [{ id: "servizio", label: cfg.listino[0].label, sub: `${route.km_ar} km A/R + noleggio banchi + montaggio/smontaggio`, qty: 1, prezzo: costoServizio, totale: costoServizio }]
      : []),
    ...(banchiExtra > 0
      ? [{ id: "banco-extra", label: "Banco di mescita aggiuntivo", sub: "un banco ogni 4 erogatori alla spina", qty: banchiExtra, prezzo: cfg.prezzo_banco_extra, totale: banchiExtra * cfg.prezzo_banco_extra }]
      : []),
    ...prodottiSel.map((p) => ({
      id: `fusto-${p.id}`,
      label: `Fusto ${p.name}`,
      sub: `20 L · circa ${cfg.porzioni_fusto} bicchieri — fusto pagato per intero`,
      qty: fusti[p.id],
      prezzo: cfg.prezzo_fusto,
      totale: fusti[p.id] * cfg.prezzo_fusto,
    })),
    ...(pacchiBicchieri > 0
      ? [{ id: "bicchieri", label: "Bicchieri (automatici)", sub: `${pacchiBicchieri * 100} pz — multipli di 100 in eccesso sulle porzioni`, qty: pacchiBicchieri, prezzo: 15, totale: pacchiBicchieri * 15 }]
      : []),
    ...(allestimento > 0
      ? [{ id: "allestimento", label: "Allestimento scenografico", sub: "", qty: 1, prezzo: 150, totale: 150 }]
      : []),
  ];
  const imponibile = voci.reduce((s, v) => s + v.totale, 0);
  const iva = Math.round(imponibile * (cfg.iva / 100) * 100) / 100;
  const totale = Math.round((imponibile + iva) * 100) / 100;
  const sede = cfg.sede_coords?.display_name || cfg.sede;

  const submit = async (e) => {
    e.preventDefault();
    if (!route) return toast.error("Scegli la località dell'evento");
    if (prodottiSel.length === 0) return toast.error("Scegli almeno un prodotto alla spina");
    if (contact.nome_cognome.trim().length < 2 || !/^\S+@\S+\.\S+$/.test(contact.email) || contact.telefono.replace(/\D/g, "").length < 8)
      return toast.error("Compila nome, email e telefono validi");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/preventivi`, {
        ...contact,
        destinazione: route.destinazione,
        km_andata: route.km_andata,
        km_ar: route.km_ar,
        voci,
        imponibile,
        iva_percento: cfg.iva,
        iva,
        totale,
      });
      setQuoteId(data.id || null);
      setDone(true);
      toast.success("Preventivo inviato! Ti ricontattiamo per confermare.");
    } catch (err) {
      toast.error("Invio non riuscito. Riprova o scrivici su WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  return (
    <article data-testid="preventivo-page">
      <section className="pt-32 md:pt-44 pb-12">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Eyebrow>Preventivo online</Eyebrow>
          <MaskLines
            lines={["CALCOLA IL TUO", "PREVENTIVO."]}
            as="h1"
            when="load"
            delay={0.15}
            lineClass="font-display font-semibold tracking-tight leading-[1] text-[clamp(2.6rem,6.5vw,5.5rem)]"
            className="mt-6"
          />
          <Reveal delay={0.3}>
            <p className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-white/65">
              Località e prodotti alla spina: il totale si calcola subito, IVA inclusa.
              Bicchieri automatici in base ai fusti (20 L, circa 100 bicchieri ciascuno), ghiaccio incluso.
              Ogni 4 erogatori scatta un banco di mescita aggiuntivo.
            </p>
          </Reveal>
        </div>
      </section>

      {done ? (
        <section className="pb-24 md:pb-32">
          <div className="mx-auto max-w-2xl px-5 md:px-8">
            <div data-testid="preventivo-success" className="rounded-3xl border border-brand-orange/40 bg-brand-orange/[0.06] p-10 md:p-14 text-center">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-brand-orange text-brand-ink">
                <Check size={26} strokeWidth={3} />
              </span>
              <h3 className="mt-6 font-display text-4xl font-semibold">Preventivo inviato!</h3>
              <p className="mt-4 text-sm leading-relaxed text-white/60">
                Totale calcolato: <span className="font-semibold text-brand-orange">{eur(totale)} IVA inclusa</span>.
                Ti ricontattiamo per confermare dettagli e disponibilità.
              </p>
              {quoteId && (
                <a
                  data-testid="preventivo-download-pdf"
                  href={`${API}/preventivi/${quoteId}/pdf`}
                  download
                  className="mt-8 inline-flex items-center justify-center gap-3 rounded-full border border-brand-orange px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-brand-orange transition-colors duration-300 hover:bg-brand-orange hover:text-brand-ink"
                >
                  <Download size={15} /> Scarica il preventivo (PDF)
                </a>
              )}
            </div>
          </div>
        </section>
      ) : (
        <section className="pb-24 md:pb-32">
          <div className="mx-auto max-w-7xl px-5 md:px-8 grid gap-10 lg:grid-cols-12 items-start">
            <div className="lg:col-span-7 space-y-10">
              <Reveal>
                <div className="rounded-3xl border border-white/[0.08] bg-brand-surface/40 p-8 md:p-10">
                  <p className="eyebrow mb-5">1 · Dove si svolge l'evento?</p>
                  <div className="relative">
                    <MapPin size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/35" />
                    <input
                      data-testid="preventivo-input-location"
                      className="field pl-11"
                      placeholder="Es. Firenze, Grosseto, Piazza del Campo Siena…"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      autoComplete="off"
                    />
                    {sugg.length > 0 && (
                      <div className="absolute z-20 left-0 right-0 top-full mt-2 rounded-2xl border border-white/10 bg-brand-ink/95 backdrop-blur-xl overflow-hidden shadow-2xl shadow-black/60">
                        {sugg.map((s, i) => (
                          <button
                            type="button"
                            key={i}
                            data-testid={`preventivo-suggestion-${i}`}
                            onClick={() => pickLocation(s.display_name)}
                            className="block w-full text-left px-5 py-3.5 text-sm text-white/70 hover:bg-white/[0.05] hover:text-white transition-colors duration-150"
                          >
                            {s.display_name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {loadingRoute && (
                    <p className="mt-4 flex items-center gap-2 text-sm text-white/50">
                      <Loader2 size={14} className="animate-spin" /> Calcolo del percorso stradale…
                    </p>
                  )}
                  {route && !loadingRoute && (
                    <div data-testid="preventivo-km-panel" className="mt-6 rounded-2xl border border-brand-orange/30 bg-brand-orange/[0.05] p-6">
                      <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/50 mb-4">
                        <Navigation size={13} className="text-brand-orange" /> Percorso stradale consigliato
                      </p>
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <p className="font-display text-2xl md:text-3xl font-semibold text-brand-orange">{route.km_andata}</p>
                          <p className="text-[11px] text-white/50 mt-1">km sola andata</p>
                        </div>
                        <div>
                          <p className="font-display text-2xl md:text-3xl font-semibold text-brand-orange">{route.km_ar}</p>
                          <p className="text-[11px] text-white/50 mt-1">km A/R</p>
                        </div>
                        <div>
                          <p className="font-display text-2xl md:text-3xl font-semibold text-brand-orange">{eur(route.costo_trasferta)}</p>
                          <p className="text-[11px] text-white/50 mt-1">trasferta</p>
                        </div>
                      </div>
                      <p className="mt-4 text-[11px] text-white/40">
                        Partenza: {sede} · tariffa {eur(cfg.tariffa_km)}/km A/R, minimo {eur(cfg.trasferta_minima)}
                      </p>
                    </div>
                  )}
                  {!route && !loadingRoute && (
                    <p className="mt-4 text-xs text-white/40">
                      Inserisci la località: la trasferta (A/R, minimo {eur(cfg.trasferta_minima)}) si somma a noleggio e montaggio.
                    </p>
                  )}
                </div>
              </Reveal>

              <Reveal delay={0.05}>
                <div className="rounded-3xl border border-white/[0.08] bg-brand-surface/40 p-8 md:p-10">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                    <p className="eyebrow">2 · Clicca i prodotti alla spina</p>
                    <p className="text-[11px] text-white/45 flex items-center gap-1.5">
                      <GlassWater size={13} className="text-brand-orange" /> Ghiaccio incluso nel costo del drink
                    </p>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {PRODOTTI.map((p) => {
                      const n = fusti[p.id] || 0;
                      return (
                        <div
                          key={p.id}
                          className={`rounded-2xl border p-4 transition-colors duration-200 ${n > 0 ? "border-brand-orange bg-brand-orange/[0.08]" : "border-white/10 bg-white/[0.03] hover:border-white/30"}`}
                        >
                          <button
                            type="button"
                            data-testid={`preventivo-prodotto-${p.id}`}
                            onClick={() => setFusti((f) => ({ ...f, [p.id]: (f[p.id] || 0) + 1 }))}
                            className="text-left w-full"
                          >
                            <p className={`font-display text-xl font-semibold ${n > 0 ? "text-brand-orange" : "text-white/85"}`}>{p.name}</p>
                            <p className="text-[11px] text-white/45 mt-0.5">{p.tipo}</p>
                          </button>
                          {n > 0 && (
                            <div className="mt-3 flex items-center justify-between gap-2">
                              <span className="text-[11px] text-white/55" data-testid={`preventivo-fusto-count-${p.id}`}>
                                {n} {n === 1 ? "fusto" : "fusti"} · circa {n * cfg.porzioni_fusto} bicchieri
                              </span>
                              <div className="flex items-center gap-1.5">
                                <button
                                  type="button"
                                  data-testid={`preventivo-fusto-minus-${p.id}`}
                                  onClick={() => setFusti((f) => ({ ...f, [p.id]: Math.max(0, f[p.id] - 1) }))}
                                  className="h-7 w-7 rounded-full border border-white/20 text-white/60 hover:border-brand-orange hover:text-brand-orange transition-colors duration-200"
                                  aria-label={`Rimuovi un fusto di ${p.name}`}
                                >
                                  <Minus size={12} className="mx-auto" />
                                </button>
                                <button
                                  type="button"
                                  data-testid={`preventivo-fusto-plus-${p.id}`}
                                  onClick={() => setFusti((f) => ({ ...f, [p.id]: (f[p.id] || 0) + 1 }))}
                                  className="h-7 w-7 rounded-full border border-white/20 text-white/60 hover:border-brand-orange hover:text-brand-orange transition-colors duration-200"
                                  aria-label={`Aggiungi un altro fusto di ${p.name}`}
                                >
                                  <Plus size={12} className="mx-auto" />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <p className="mt-5 text-[11px] text-white/40">
                    Fusti da 20 L: circa {cfg.porzioni_fusto} bicchieri per fusto, {eur(cfg.prezzo_fusto)} a fusto, pagato per intero. Ogni prodotto occupa un erogatore: oltre 4 scatta un banco aggiuntivo.
                  </p>
                </div>
              </Reveal>

              <Reveal delay={0.05}>
                <div className="rounded-3xl border border-white/[0.08] bg-brand-surface/40 p-8 md:p-10">
                  <p className="eyebrow mb-6">3 · Extra</p>
                  <div className="space-y-0">
                    <div className="flex items-center justify-between gap-4 border-b border-white/[0.07] py-4">
                      <div>
                        <p className="text-sm md:text-base text-white">Banco aggiuntivo (automatico)</p>
                        <p className="text-[11px] text-white/40 mt-0.5">Un banco ogni 4 erogatori, oltre il primo · {eur(cfg.prezzo_banco_extra)} a banco</p>
                      </div>
                      <span data-testid="preventivo-banco-extra-auto" className="text-sm whitespace-nowrap">
                        {erogatori > 4 ? (
                          <span className="text-brand-orange font-semibold">{banchiExtra} banc{banchiExtra > 1 ? "i" : "o"} · {eur(banchiExtra * cfg.prezzo_banco_extra)}</span>
                        ) : (
                          <span className="text-white/40">incluso fino a 4 erogatori</span>
                        )}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-4 border-b border-white/[0.07] py-4">
                      <div>
                        <p className="text-sm md:text-base text-white">Bicchieri (automatici)</p>
                        <p className="text-[11px] text-white/40 mt-0.5">Multipli di 100 in eccesso sulle porzioni acquistate · 15,00 € / 100 pz</p>
                      </div>
                      <span data-testid="preventivo-bicchieri-auto" className="text-sm whitespace-nowrap">
                        {totPorzioni > 0 ? (
                          <span className="text-brand-orange font-semibold">{pacchiBicchieri * 100} pz · {eur(pacchiBicchieri * 15)}</span>
                        ) : (
                          <span className="text-white/40">si calcolano dopo i fusti</span>
                        )}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-4 py-4">
                      <div>
                        <p className="text-sm md:text-base text-white">Allestimento scenografico</p>
                        <p className="text-[11px] text-white/40 mt-0.5">Veste wedding o coordinata all'evento · 150,00 €</p>
                      </div>
                      <button
                        type="button"
                        data-testid="preventivo-allestimento-toggle"
                        onClick={() => setAllestimento((a) => (a === 0 ? 1 : 0))}
                        className={`h-9 px-5 rounded-full border text-[11px] font-semibold uppercase tracking-[0.15em] transition-colors duration-200 ${allestimento ? "border-brand-orange bg-brand-orange text-brand-ink" : "border-white/15 text-white/60 hover:border-brand-orange hover:text-brand-orange"}`}
                      >
                        {allestimento ? "Sì" : "No"}
                      </button>
                    </div>
                  </div>
                </div>
              </Reveal>

              <Reveal delay={0.05}>
                <div className="rounded-3xl border border-white/[0.08] bg-brand-surface/40 p-8 md:p-10">
                  <p className="eyebrow mb-6">4 · Ti ricontattiamo per confermare</p>
                  <div className="grid gap-6 md:grid-cols-2">
                    <input data-testid="preventivo-field-nome" className="field" placeholder="Nome e cognome *" value={contact.nome_cognome} onChange={(e) => setContact((c) => ({ ...c, nome_cognome: e.target.value }))} />
                    <input data-testid="preventivo-field-email" type="email" className="field" placeholder="Email *" value={contact.email} onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))} />
                    <input data-testid="preventivo-field-telefono" type="tel" className="field" placeholder="Telefono *" value={contact.telefono} onChange={(e) => setContact((c) => ({ ...c, telefono: e.target.value }))} />
                    <input data-testid="preventivo-field-data" type="date" className="field [color-scheme:dark]" value={contact.data_evento} onChange={(e) => setContact((c) => ({ ...c, data_evento: e.target.value }))} />
                  </div>
                  <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={contact.website} onChange={(e) => setContact((c) => ({ ...c, website: e.target.value }))} className="hidden" />
                  <button
                    type="submit"
                    data-testid="preventivo-submit"
                    onClick={submit}
                    disabled={sending}
                    className="group mt-8 inline-flex w-full md:w-auto items-center justify-center gap-3 rounded-full bg-brand-orange px-10 py-5 text-xs font-semibold uppercase tracking-[0.2em] text-brand-ink shadow-[0_0_50px_rgba(255,121,0,0.3)] transition-colors duration-300 hover:bg-[#ff9433] disabled:opacity-60"
                  >
                    {sending ? (
                      <>
                        <Loader2 size={16} className="animate-spin" /> Invio…
                      </>
                    ) : (
                      <>Invia il preventivo · {eur(totale)}</>
                    )}
                  </button>
                  <p className="mt-4 text-xs text-white/40">
                    Preventivo indicativo e confermabile: la proposta finale viene definita insieme.
                  </p>
                </div>
              </Reveal>
            </div>

            <aside className="lg:col-span-5 lg:sticky lg:top-28">
              <Reveal delay={0.1}>
                <div data-testid="preventivo-recap" className="rounded-3xl border border-brand-orange/30 bg-brand-coal p-8 md:p-10">
                  <p className="eyebrow mb-6">Il tuo preventivo</p>
                  {voci.length === 0 ? (
                    <p className="text-sm text-white/40" data-testid="preventivo-empty">
                      Scegli la località e i prodotti: il riepilogo si costruisce qui.
                    </p>
                  ) : (
                    <div className="space-y-0">
                      {voci.map((v) => (
                        <div key={v.id} className="border-b border-white/[0.07] py-3.5">
                          <div className="flex items-baseline justify-between gap-4">
                            <span className="text-sm text-white/70">
                              {v.label}
                              <span className="text-white/40"> {v.qty > 1 ? `· ×${v.qty}` : ""}</span>
                            </span>
                            <span className="text-sm text-white/90 whitespace-nowrap">{eur(v.totale)}</span>
                          </div>
                          {v.sub && <p className="text-[11px] text-white/35 mt-1">{v.sub}</p>}
                        </div>
                      ))}
                      <div className="flex items-baseline justify-between gap-4 pt-5">
                        <span className="text-sm text-white/50">Imponibile</span>
                        <span data-testid="preventivo-imponibile" className="text-sm text-white/80">{eur(imponibile)}</span>
                      </div>
                      <div className="flex items-baseline justify-between gap-4 py-2">
                        <span className="text-sm text-white/50">IVA {cfg.iva}%</span>
                        <span data-testid="preventivo-iva" className="text-sm text-white/80">{eur(iva)}</span>
                      </div>
                      <div className="mt-4 rounded-2xl bg-brand-orange p-5 flex items-baseline justify-between gap-4">
                        <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-ink">Totale IVA inclusa</span>
                        <span data-testid="preventivo-totale" className="font-display text-3xl font-bold text-brand-ink">{eur(totale)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </Reveal>
            </aside>
          </div>
        </section>
      )}
    </article>
  );
}
